import { useEffect, useRef } from "react";
import { useEditorStore } from "../store";

export interface UseAutosaveOptions {
  onSave?: (doc: ReturnType<typeof useEditorStore.getState>["document"]) => Promise<void>;
  debounceMs?: number;
}

export function useAutosave({ onSave, debounceMs = 1500 }: UseAutosaveOptions = {}) {
  const isDirty = useEditorStore((s) => s.isDirty);
  const document = useEditorStore((s) => s.document);
  const setSaveStatus = useEditorStore((s) => s.setSaveStatus);
  const markSaved = useEditorStore((s) => s.markSaved);
  const saveAttempt = useEditorStore((s) => s.saveAttempt);

  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastAttemptRef = useRef(saveAttempt);
  // Saves run one at a time: each request carries the revision the previous
  // one returned, so overlapping requests would be rejected as conflicts.
  const queueRef = useRef<Promise<void>>(Promise.resolve());
  // Hosts often pass an inline onSave; a ref keeps it from restarting the debounce.
  const onSaveRef = useRef(onSave);
  onSaveRef.current = onSave;

  useEffect(() => {
    if (!isDirty) return;

    if (timerRef.current) clearTimeout(timerRef.current);

    // A retry the user asked for runs now rather than after the edit debounce.
    const isRetry = lastAttemptRef.current !== saveAttempt;
    lastAttemptRef.current = saveAttempt;

    const save = async () => {
      // Save what is in the editor when this save starts, not when it was scheduled.
      const saving = useEditorStore.getState().document;
      setSaveStatus("saving");
      try {
        if (onSaveRef.current) {
          await onSaveRef.current(saving);
        }
        const state = useEditorStore.getState();
        if (state.document === saving) {
          markSaved();
        } else if (state.isDirty) {
          // Edited while saving: those edits still need their own save.
          setSaveStatus("unsaved");
        }
      } catch (err) {
        console.error("Autosave failed:", err);
        setSaveStatus(
          "error",
          err instanceof Error && err.message
            ? err.message
            : "Your changes couldn't be saved. They're kept in this browser — try again in a moment.",
        );
      }
    };

    timerRef.current = setTimeout(() => {
      queueRef.current = queueRef.current.then(save);
    }, isRetry ? 0 : debounceMs);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [isDirty, document, debounceMs, setSaveStatus, markSaved, saveAttempt]);

  // Warn before browser window or tab closes with unsaved changes (PRD §146)
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (useEditorStore.getState().isDirty) {
        e.preventDefault();
        e.returnValue = "You have unsaved changes. Are you sure you want to leave?";
        return e.returnValue;
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);
}
